/**
 * Admin sub-page — send a one-off HTML email to an arbitrary recipient list
 * (prospects, not existing app users) and track delivery.
 *
 * Mounted as a tab inside /admin (see UserModuleAdmin.tsx). Sending reuses
 * the same Mailgun pipe as pilot invitations: Reply-To is the sending
 * admin's own address, so a reply lands in a real inbox, not `support@`.
 */
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Heading,
  Icon,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  Wrap,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  LuCheck,
  LuClipboardCopy,
  LuClock,
  LuCopy,
  LuEye,
  LuList,
  LuMegaphone,
  LuPlus,
  LuRefreshCw,
  LuSend,
  LuTrash2,
  LuTriangleAlert,
  LuUserX,
} from 'react-icons/lu'
import {
  CampaignList,
  useCampaignLists,
  useCreateCampaignList,
  useDeleteCampaignList,
} from '../../api/api_hooks/admin/campaignLists'
import {
  Campaign,
  CampaignStatus,
  RecipientStatus,
  useCampaignSuppressions,
  useCampaigns,
  useCreateCampaign,
  useDeleteCampaign,
} from '../../api/api_hooks/admin/campaigns'
import { useMyProfile } from '../../api/api_hooks/user/myProfile'
import { CHROME_HEADER_GRADIENT, CHROME_GLASS_BORDER } from '../../theme/chrome'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Same split logic as the backend — one email per line/comma/semicolon, deduped. */
const splitEmails = (raw: string) => {
  const items = raw
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean)
  return Array.from(new Set(items.map((s) => s.toLowerCase())))
}

const countValidEmails = (raw: string) => splitEmails(raw).filter((e) => EMAIL_RE.test(e)).length

const STATUS_TONE: Record<CampaignStatus, { scheme: string; icon: any; label: string }> = {
  queued: { scheme: 'gray', icon: LuClock, label: 'En attente' },
  sending: { scheme: 'blue', icon: LuSend, label: 'Envoi en cours' },
  completed: { scheme: 'green', icon: LuCheck, label: 'Envoyée' },
  failed: { scheme: 'red', icon: LuTriangleAlert, label: 'Échec' },
}

export default function CampaignsAdmin() {
  const toast = useToast()
  const { data: campaigns, isLoading } = useCampaigns()
  const { data: suppressions } = useCampaignSuppressions()
  const { data: lists } = useCampaignLists()
  const createMutation = useCreateCampaign()
  const deleteMutation = useDeleteCampaign()
  const createListMutation = useCreateCampaignList()
  const deleteListMutation = useDeleteCampaignList()
  const { data: me } = useMyProfile()

  const compose = useDisclosure()
  const confirmSend = useDisclosure()
  const deleteDialog = useDisclosure()
  const saveListDialog = useDisclosure()
  const [newListName, setNewListName] = useState('')
  const cancelRef = useRef<HTMLButtonElement>(null)
  const composeTopRef = useRef<HTMLDivElement>(null)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  // Store only the id, not the row object — the row is a snapshot from the
  // moment of the click. While the modal is open, a send can keep
  // progressing in the background; deriving from the live list on every
  // render (instead of freezing the clicked object) keeps it in sync.
  const [detailCampaignId, setDetailCampaignId] = useState<string | null>(null)
  const detailCampaign = campaigns?.data.find((c) => c.id === detailCampaignId) ?? null

  const [form, setForm] = useState({ name: '', subject: '', recipients: '', html: '' })
  const validCount = useMemo(() => countValidEmails(form.recipients), [form.recipients])
  const unsubscribedInList = useMemo(() => {
    if (!suppressions) return []
    return splitEmails(form.recipients).filter((e) => suppressions.has(e))
  }, [form.recipients, suppressions])

  const removeUnsubscribed = () => {
    const toDrop = new Set(unsubscribedInList)
    const kept = splitEmails(form.recipients).filter((e) => !toDrop.has(e))
    setForm((f) => ({ ...f, recipients: kept.join('\n') }))
  }

  /** Insert a saved list's addresses into the recipients field, merged with whatever's already typed. */
  const insertList = (list: CampaignList) => {
    const merged = Array.from(new Set([...splitEmails(form.recipients), ...list.emails]))
    setForm((f) => ({ ...f, recipients: merged.join('\n') }))
    toast({
      title: `Liste « ${list.name} » insérée — ${list.emails.length} adresse(s)`,
      // Une liste dynamique est relue à chaque ouverture de ce menu : le dire
      // ici évite de se demander si elle est à jour.
      description: list.dynamic ? 'Liste des comptes de l’application, relue à l’instant.' : undefined,
      status: 'success',
      duration: 2500,
    })
  }

  const handleSaveAsList = async () => {
    if (!newListName.trim() || validCount === 0) return
    try {
      const res = await createListMutation.mutateAsync({ name: newListName.trim(), emails: form.recipients })
      saveListDialog.onClose()
      setNewListName('')
      toast({ title: `Liste « ${res.name} » enregistrée — ${res.count} adresse(s)`, status: 'success' })
    } catch (error: any) {
      toast({ title: 'Enregistrement impossible', description: error?.response?.data?.message, status: 'error' })
    }
  }

  const handleDeleteList = async (list: CampaignList, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await deleteListMutation.mutateAsync(list.id)
      toast({ title: `Liste « ${list.name} » supprimée`, status: 'success' })
    } catch (error: any) {
      toast({ title: 'Suppression impossible', description: error?.response?.data?.message, status: 'error' })
    }
  }

  const canSend = form.name.trim() && form.subject.trim() && form.html.trim() && validCount > 0

  const resetForm = () => setForm({ name: '', subject: '', recipients: '', html: '' })

  /**
   * "Réexploiter" — prefill the compose form from a past campaign (subject,
   * HTML, and the same recipients) instead of starting from a blank page.
   * The admin still reviews/edits before sending; nothing goes out yet.
   */
  const handleDuplicate = (c: Campaign) => {
    setForm({
      name: `${c.name} (copie)`,
      subject: c.subject,
      recipients: c.recipients.map((r) => r.email).join('\n'),
      html: c.html,
    })
    compose.onOpen()
    // Let the panel mount before scrolling to it.
    requestAnimationFrame(() => composeTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }

  /**
   * "Réessayer les échecs" — same idea as handleDuplicate, but scoped to
   * only the recipients that failed (typically a transient Mailgun
   * rate-limit, not a bad address — see SEND_DELAY_MS in admin-campaigns.js).
   * Reopens compose prefilled so the admin still reviews before resending.
   */
  const handleRetryFailed = (c: Campaign) => {
    const failedEmails = c.recipients.filter((r) => r.status === 'failed').map((r) => r.email)
    if (!failedEmails.length) return
    setForm({
      name: `${c.name} (relance échecs)`,
      subject: c.subject,
      recipients: failedEmails.join('\n'),
      html: c.html,
    })
    compose.onOpen()
    requestAnimationFrame(() => composeTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    toast({ title: label, status: 'success', duration: 2000 })
  }

  const handleSend = async () => {
    try {
      const res = await createMutation.mutateAsync(form)
      confirmSend.onClose()
      compose.onClose()
      resetForm()
      if (res.invalidRecipients.length) {
        toast({
          title: `${res.invalidRecipients.length} adresse(s) ignorée(s) (format invalide)`,
          description: res.invalidRecipients.slice(0, 5).join(', '),
          status: 'warning',
          duration: 6000,
          isClosable: true,
        })
      }
      toast({ title: `Campagne lancée — envoi à ${res.totalCount - res.skippedCount} destinataire(s)`, status: 'success' })
    } catch (error: any) {
      toast({ title: 'Envoi impossible', description: error?.response?.data?.message, status: 'error' })
    }
  }

  const handleDelete = async () => {
    if (!pendingDeleteId) return
    try {
      await deleteMutation.mutateAsync(pendingDeleteId)
      toast({ title: 'Campagne supprimée', status: 'success' })
    } catch (error: any) {
      toast({ title: 'Suppression impossible', description: error?.response?.data?.message, status: 'error' })
    } finally {
      deleteDialog.onClose()
      setPendingDeleteId(null)
    }
  }

  return (
    <VStack spacing={5} align="stretch">
      {/* ── Header ── */}
      <Card borderRadius="xl" borderWidth="1px" borderColor="gray.100" overflow="hidden">
        <Box bgGradient={CHROME_HEADER_GRADIENT} p={5} position="relative" overflow="hidden" sx={{ '&::after': CHROME_GLASS_BORDER }}>
          <HStack justify="space-between" align="center">
            <HStack spacing={3}>
              <Box w="44px" h="44px" borderRadius="xl" bg="whiteAlpha.300" display="grid" sx={{ placeItems: 'center' }} backdropFilter="blur(8px)">
                <Icon as={LuMegaphone} boxSize={5} color="white" />
              </Box>
              <VStack align="start" spacing={0}>
                <Heading size="md" color="white">Campagnes</Heading>
                <Text fontSize="xs" color="whiteAlpha.900">
                  Envoyez un email HTML à une liste de destinataires — les réponses arrivent dans votre boîte
                </Text>
              </VStack>
            </HStack>
            <Button
              leftIcon={<LuPlus />}
              onClick={compose.isOpen ? compose.onClose : compose.onOpen}
              bg="white"
              color="purple.700"
              _hover={{ bg: 'whiteAlpha.900', transform: 'translateY(-1px)' }}
              borderRadius="xl"
              fontWeight="700"
            >
              {compose.isOpen ? 'Fermer' : 'Nouvelle campagne'}
            </Button>
          </HStack>
        </Box>
      </Card>

      {/* ── Compose ── */}
      {compose.isOpen && (
        <Card borderRadius="xl" borderWidth="1px" borderColor="gray.200" ref={composeTopRef}>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Nom interne</FormLabel>
                  <Input
                    placeholder="Ex. IRIS — vague CHU septembre"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    bg="white"
                    borderRadius="lg"
                  />
                  <FormHelperText>Jamais montré aux destinataires.</FormHelperText>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Sujet</FormLabel>
                  <Input
                    placeholder="Ex. IRIS ne se contente plus de lire vos immunoblots"
                    value={form.subject}
                    onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                    bg="white"
                    borderRadius="lg"
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired>
                <HStack justify="space-between" mb={1}>
                  <FormLabel fontSize="sm" mb={0}>Destinataires</FormLabel>
                  <HStack spacing={2}>
                    <Menu placement="bottom-end">
                      <MenuButton as={Button} size="xs" variant="outline" colorScheme="purple" leftIcon={<LuList />}>
                        Insérer une liste
                      </MenuButton>
                      <MenuList minW="260px">
                        {!lists?.length ? (
                          <Text fontSize="xs" color="gray.500" px={3} py={2}>
                            Aucune liste enregistrée — utilisez « Enregistrer comme liste » une fois des adresses saisies.
                          </Text>
                        ) : (
                          lists.map((list) => (
                            <MenuItem key={list.id} onClick={() => insertList(list)}>
                              <HStack justify="space-between" flex={1}>
                                <HStack spacing={2}>
                                  <Text fontSize="sm" fontWeight="600">{list.name}</Text>
                                  <Badge borderRadius="md" colorScheme="gray">{list.count}</Badge>
                                  {/* Une liste dynamique se recalcule sur les comptes de
                                      l'application : elle n'a pas d'adresses à éditer, et
                                      un nouveau compte y entre sans rien faire. */}
                                  {list.dynamic && (
                                    <Badge borderRadius="md" colorScheme="purple" title="Les comptes de l’application, recalculés à chaque ouverture">
                                      auto
                                    </Badge>
                                  )}
                                </HStack>
                                {!list.dynamic && (
                                  <IconButton
                                    aria-label={`Supprimer la liste ${list.name}`}
                                    icon={<LuTrash2 />}
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={(e) => handleDeleteList(list, e)}
                                  />
                                )}
                              </HStack>
                            </MenuItem>
                          ))
                        )}
                        <MenuDivider />
                        <MenuItem icon={<LuPlus />} isDisabled={validCount === 0} onClick={saveListDialog.onOpen}>
                          Enregistrer comme liste
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </HStack>
                </HStack>
                <Textarea
                  placeholder={'un email par ligne (ou séparés par des virgules)\nprospect1@chu-exemple.fr\nprospect2@labo-exemple.fr'}
                  value={form.recipients}
                  onChange={(e) => setForm((f) => ({ ...f, recipients: e.target.value }))}
                  bg="white"
                  borderRadius="lg"
                  rows={4}
                  fontFamily="mono"
                  fontSize="sm"
                />
                <FormHelperText>
                  {validCount > 0 ? (
                    <Text as="span" color="green.600" fontWeight="600">{validCount} adresse(s) valide(s) détectée(s)</Text>
                  ) : (
                    'Aucune adresse valide détectée pour le moment.'
                  )}
                </FormHelperText>
                {unsubscribedInList.length > 0 && (
                  <Box mt={2} p={2} borderRadius="md" bg="orange.50">
                    <HStack spacing={3}>
                      <Icon as={LuUserX} color="orange.500" boxSize={4} />
                      <Text fontSize="xs" color="orange.700" flex={1}>
                        {unsubscribedInList.length} adresse(s) déjà désinscrite(s) dans cette liste — elles seront ignorées à l&apos;envoi, mais vous pouvez les retirer maintenant.
                      </Text>
                      <Button size="xs" variant="outline" colorScheme="orange" onClick={removeUnsubscribed}>
                        Retirer
                      </Button>
                    </HStack>
                    {/* LESQUELLES — un compte seul obligeait à deviner. */}
                    <HStack mt={1.5} pl={7} spacing={1.5} flexWrap="wrap">
                      {unsubscribedInList.map((email) => (
                        <Badge key={email} colorScheme="orange" variant="subtle" textTransform="none" fontSize="2xs" borderRadius="md">
                          {email}
                        </Badge>
                      ))}
                    </HStack>
                  </Box>
                )}
              </FormControl>

              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">HTML de l&apos;email</FormLabel>
                  <Textarea
                    placeholder="Collez ici le code HTML complet de l'email…"
                    value={form.html}
                    onChange={(e) => setForm((f) => ({ ...f, html: e.target.value }))}
                    bg="white"
                    borderRadius="lg"
                    rows={16}
                    fontFamily="mono"
                    fontSize="xs"
                  />
                  <FormHelperText>
                    Le jeton <code>%unsubscribe_url%</code> est remplacé automatiquement par un lien de désinscription propre à chaque destinataire.
                  </FormHelperText>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Aperçu</FormLabel>
                  <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden" h="368px" bg="gray.50">
                    {form.html.trim() ? (
                      <iframe
                        title="Aperçu de l'email"
                        srcDoc={form.html}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                      />
                    ) : (
                      <HStack h="100%" justify="center">
                        <Text fontSize="sm" color="gray.400">L&apos;aperçu apparaît ici une fois le HTML collé</Text>
                      </HStack>
                    )}
                  </Box>
                </FormControl>
              </SimpleGrid>

              <Divider />

              <HStack justify="space-between">
                <Text fontSize="xs" color="gray.500">
                  Les réponses seront reçues sur <strong>{me?.email || 'votre adresse'}</strong>.
                </Text>
                <Button
                  leftIcon={<LuSend />}
                  colorScheme="purple"
                  borderRadius="lg"
                  fontWeight="700"
                  isDisabled={!canSend}
                  onClick={confirmSend.onOpen}
                >
                  Envoyer la campagne
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* ── History ── */}
      <Card borderRadius="xl" borderWidth="1px" borderColor="gray.200">
        <CardBody p={0}>
          {isLoading ? (
            <HStack p={8} justify="center"><Text color="gray.600">Chargement…</Text></HStack>
          ) : !campaigns?.data.length ? (
            <VStack p={10} spacing={3}>
              <Icon as={LuMegaphone} boxSize={8} color="gray.300" />
              <Text color="gray.600" fontWeight="600">Aucune campagne pour l&apos;instant</Text>
              <Text fontSize="sm" color="gray.500">Cliquez sur « Nouvelle campagne » pour envoyer votre premier email.</Text>
            </VStack>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Campagne</Th>
                    <Th>Destinataires</Th>
                    <Th>Statut</Th>
                    <Th>Envoyée par</Th>
                    <Th>Date</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {campaigns.data.map((c) => (
                    <CampaignRow
                      key={c.id}
                      campaign={c}
                      onViewDetails={() => setDetailCampaignId(c.id)}
                      onDuplicate={() => handleDuplicate(c)}
                      onRetryFailed={() => handleRetryFailed(c)}
                      onDelete={() => { setPendingDeleteId(c.id); deleteDialog.onOpen() }}
                    />
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* ── Recipient details ── */}
      <CampaignDetailModal
        campaign={detailCampaign}
        onClose={() => setDetailCampaignId(null)}
        onRetryFailed={() => {
          if (detailCampaign) handleRetryFailed(detailCampaign)
          setDetailCampaignId(null)
        }}
        onCopy={copyToClipboard}
      />

      {/* ── Confirm send ── */}
      <AlertDialog isOpen={confirmSend.isOpen} leastDestructiveRef={cancelRef} onClose={confirmSend.onClose} isCentered>
        <AlertDialogOverlay />
        <AlertDialogContent borderRadius="xl">
          <AlertDialogHeader>Envoyer la campagne ?</AlertDialogHeader>
          <AlertDialogBody>
            Cet email va être envoyé à <strong>{validCount} destinataire(s)</strong>. Cette action ne peut pas être annulée.
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={confirmSend.onClose}>Annuler</Button>
            <Button colorScheme="purple" onClick={handleSend} ml={3} isLoading={createMutation.isPending} leftIcon={<LuSend />}>
              Envoyer
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Confirm delete ── */}
      <AlertDialog isOpen={deleteDialog.isOpen} leastDestructiveRef={cancelRef} onClose={deleteDialog.onClose} isCentered>
        <AlertDialogOverlay />
        <AlertDialogContent borderRadius="xl">
          <AlertDialogHeader>Supprimer cette campagne ?</AlertDialogHeader>
          <AlertDialogBody>
            Ceci supprime uniquement l&apos;historique — les emails déjà envoyés ne peuvent pas être rappelés.
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={deleteDialog.onClose}>Annuler</Button>
            <Button colorScheme="red" onClick={handleDelete} ml={3} isLoading={deleteMutation.isPending} leftIcon={<LuTrash2 />}>
              Supprimer
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Save current recipients as a list ── */}
      <Modal isOpen={saveListDialog.isOpen} onClose={saveListDialog.onClose} isCentered size="sm">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>Enregistrer comme liste</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Les {validCount} adresse(s) valide(s) du champ Destinataires seront enregistrées sous ce nom, réutilisable dans une future campagne.
            </Text>
            <FormControl>
              <FormLabel fontSize="sm">Nom de la liste</FormLabel>
              <Input
                placeholder="Ex. CHUs"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                bg="white"
                borderRadius="lg"
                autoFocus
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button onClick={saveListDialog.onClose}>Annuler</Button>
            <Button
              colorScheme="purple"
              ml={3}
              isDisabled={!newListName.trim()}
              isLoading={createListMutation.isPending}
              onClick={handleSaveAsList}
            >
              Enregistrer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  )
}

function CampaignRow({
  campaign: c, onViewDetails, onDuplicate, onRetryFailed, onDelete,
}: {
  campaign: Campaign
  onViewDetails: () => void
  onDuplicate: () => void
  onRetryFailed: () => void
  onDelete: () => void
}) {
  const locale = fr
  const tone = STATUS_TONE[c.status]
  const attempted = c.totalCount - c.skippedCount
  const progressPct = attempted > 0 ? Math.round(((c.sentCount + c.failedCount) / attempted) * 100) : 100
  const isDone = c.status === 'completed' || c.status === 'failed'

  return (
    <Tr>
      <Td>
        <Text fontWeight="600" fontSize="sm">{c.name}</Text>
        <Text fontSize="xs" color="gray.500" noOfLines={1}>{c.subject}</Text>
      </Td>
      <Td>
        <Text fontSize="sm">{c.totalCount}</Text>
        {c.skippedCount > 0 && (
          <Tooltip label="Désinscrits — non contactés">
            <HStack spacing={1}>
              <Icon as={LuUserX} boxSize={3} color="gray.400" />
              <Text fontSize="xs" color="gray.500">{c.skippedCount} ignoré(s)</Text>
            </HStack>
          </Tooltip>
        )}
      </Td>
      <Td minW="200px" cursor="pointer" onClick={onViewDetails}>
        <Badge colorScheme={tone.scheme} borderRadius="md" mb={1.5}>
          <HStack spacing={1}>
            <Icon as={tone.icon} boxSize={3} />
            <Text as="span">{tone.label}</Text>
          </HStack>
        </Badge>
        {(c.status === 'sending' || isDone) && (
          <>
            <Progress
              value={progressPct}
              size="xs"
              borderRadius="full"
              colorScheme={c.failedCount > 0 && c.status !== 'sending' ? 'orange' : 'purple'}
              mt={1}
            />
            {/* Colored count chips instead of a single sentence — a red
                failure count should jump out, not be buried mid-text. */}
            <HStack spacing={2} mt={1.5} flexWrap="wrap">
              <HStack spacing={0.5}>
                <Icon as={LuCheck} boxSize={3} color="green.500" />
                <Text fontSize="xs" color="green.600" fontWeight="600">{c.sentCount}</Text>
              </HStack>
              {c.failedCount > 0 && (
                <HStack spacing={0.5}>
                  <Icon as={LuTriangleAlert} boxSize={3} color="red.500" />
                  <Text fontSize="xs" color="red.600" fontWeight="700">{c.failedCount}</Text>
                </HStack>
              )}
              {c.status === 'sending' && (
                <Text fontSize="xs" color="gray.400">/ {attempted}</Text>
              )}
            </HStack>
          </>
        )}
      </Td>
      <Td><Text fontSize="sm">{c.sender?.contactName || c.sender?.email || '—'}</Text></Td>
      <Td><Text fontSize="xs" color="gray.500">{formatDistanceToNow(new Date(c.createdAt), { locale, addSuffix: true })}</Text></Td>
      <Td>
        <HStack spacing={1}>
          <Tooltip label="Voir le détail par destinataire">
            <IconButton
              aria-label="Détails"
              icon={<LuEye />}
              size="sm"
              variant="ghost"
              colorScheme="gray"
              onClick={onViewDetails}
            />
          </Tooltip>
          {isDone && c.failedCount > 0 && (
            <Tooltip label={`Réessayer uniquement les ${c.failedCount} échec(s) — recharge le formulaire, rien n'est renvoyé sans validation`}>
              <IconButton
                aria-label="Réessayer les échecs"
                icon={<LuRefreshCw />}
                size="sm"
                variant="ghost"
                colorScheme="orange"
                onClick={onRetryFailed}
              />
            </Tooltip>
          )}
          <Tooltip label="Réexploiter — recharge sujet, HTML et destinataires dans le formulaire">
            <IconButton
              aria-label="Réexploiter"
              icon={<LuCopy />}
              size="sm"
              variant="ghost"
              colorScheme="purple"
              onClick={onDuplicate}
            />
          </Tooltip>
          <IconButton
            aria-label="Supprimer"
            icon={<LuTrash2 />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            isDisabled={c.status === 'sending'}
            onClick={onDelete}
          />
        </HStack>
      </Td>
    </Tr>
  )
}

/**
 * Recipient breakdown for one campaign. Failures are grouped by error
 * message (Mailgun tends to fail a whole batch with the same reason — e.g.
 * a rate-limit — so 30 identical "status code 420" rows read as one line
 * with a count, not thirty), and a filter strip jumps straight to what
 * needs attention: defaults to "Échecs" when there are any.
 */
function CampaignDetailModal({
  campaign: c, onClose, onRetryFailed, onCopy,
}: {
  campaign: Campaign | null
  onClose: () => void
  onRetryFailed: () => void
  onCopy: (text: string, label: string) => void
}) {
  type FilterKey = 'all' | RecipientStatus
  const [filter, setFilter] = useState<FilterKey>('all')

  useEffect(() => {
    if (c) setFilter(c.failedCount > 0 ? 'failed' : 'all')
  }, [c])

  const visible = useMemo(() => {
    if (!c) return []
    return filter === 'all' ? c.recipients : c.recipients.filter((r) => r.status === filter)
  }, [c, filter])

  // Group failures by their exact error message — turns a wall of repeated
  // rows into a handful of "N × <cause>" groups.
  const failedGroups = useMemo(() => {
    if (filter !== 'failed') return []
    const map = new Map<string, string[]>()
    visible.forEach((r) => {
      const key = r.error || 'Erreur inconnue'
      map.set(key, [...(map.get(key) || []), r.email])
    })
    return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length)
  }, [visible, filter])

  if (!c) return null

  const counts = {
    all: c.totalCount,
    sent: c.sentCount,
    failed: c.failedCount,
    pending: c.recipients.filter((r) => r.status === 'pending').length,
    skipped_unsubscribed: c.skippedCount,
  }

  const FILTERS: { key: FilterKey; label: string; scheme: string }[] = [
    { key: 'all', label: 'Tous', scheme: 'gray' },
    { key: 'sent', label: 'Envoyés', scheme: 'green' },
    { key: 'failed', label: 'Échecs', scheme: 'red' },
    { key: 'pending', label: 'En attente', scheme: 'gray' },
    { key: 'skipped_unsubscribed', label: 'Désinscrits', scheme: 'orange' },
  ]

  return (
    <Modal isOpen onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalHeader>
          <Text fontSize="md">{c.name}</Text>
          <Text fontSize="xs" color="gray.500" fontWeight="400" noOfLines={1}>{c.subject}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={2}>
          {/* Stat strip */}
          <SimpleGrid columns={4} spacing={2} mb={4}>
            <StatChip label="Total" value={c.totalCount} scheme="gray" />
            <StatChip label="Envoyés" value={c.sentCount} scheme="green" />
            <StatChip label="Échecs" value={c.failedCount} scheme="red" />
            <StatChip label="Désinscrits" value={c.skippedCount} scheme="orange" />
          </SimpleGrid>

          {/* Filter strip */}
          <Wrap mb={3}>
            {FILTERS.filter((f) => f.key === 'all' || counts[f.key] > 0).map((f) => (
              <Button
                key={f.key}
                size="xs"
                borderRadius="full"
                variant={filter === f.key ? 'solid' : 'outline'}
                colorScheme={f.scheme}
                onClick={() => setFilter(f.key)}
              >
                {f.label} ({counts[f.key]})
              </Button>
            ))}
          </Wrap>

          {filter === 'failed' && failedGroups.length > 0 && (
            <HStack justify="space-between" mb={2}>
              <Text fontSize="xs" color="gray.500">Regroupés par cause — souvent la même sur toute une rafale (ex. rate-limit Mailgun)</Text>
              <Button
                size="xs"
                leftIcon={<LuClipboardCopy />}
                variant="ghost"
                colorScheme="red"
                onClick={() => onCopy(visible.map((r) => r.email).join('\n'), `${visible.length} adresse(s) en échec copiée(s)`)}
              >
                Copier toutes les adresses
              </Button>
            </HStack>
          )}

          {filter === 'failed' && failedGroups.length > 0 ? (
            <Accordion allowMultiple defaultIndex={failedGroups.map((_, i) => i)} borderWidth="1px" borderColor="gray.200" borderRadius="lg">
              {failedGroups.map(([error, emails]) => (
                <AccordionItem key={error} border="none" borderTopWidth="1px" borderColor="gray.100" _first={{ borderTopWidth: 0 }}>
                  <AccordionButton _hover={{ bg: 'red.50' }}>
                    <HStack flex={1} spacing={2}>
                      <Badge colorScheme="red" borderRadius="md">{emails.length}</Badge>
                      <Text fontSize="sm" color="red.700" fontWeight="600" noOfLines={1} textAlign="left">{error}</Text>
                    </HStack>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel bg="gray.50" maxH="200px" overflowY="auto">
                    <HStack justify="flex-end" mb={1.5}>
                      <Button
                        size="xs"
                        leftIcon={<LuCopy />}
                        variant="ghost"
                        onClick={() => onCopy(emails.join('\n'), `${emails.length} adresse(s) copiée(s)`)}
                      >
                        Copier ces adresses
                      </Button>
                    </HStack>
                    <VStack align="stretch" spacing={1}>
                      {emails.map((email) => (
                        <Text key={email} fontSize="xs" color="gray.700" fontFamily="mono">{email}</Text>
                      ))}
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <Box overflowX="auto" borderWidth="1px" borderColor="gray.200" borderRadius="lg">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Destinataire</Th>
                    <Th>Statut</Th>
                    <Th>Détail</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {visible.map((r) => {
                    const rTone: Record<string, { scheme: string; label: string }> = {
                      sent: { scheme: 'green', label: 'Envoyé' },
                      failed: { scheme: 'red', label: 'Échec' },
                      pending: { scheme: 'gray', label: 'En attente' },
                      skipped_unsubscribed: { scheme: 'orange', label: 'Désinscrit' },
                    }
                    const tone = rTone[r.status]
                    return (
                      <Tr key={r.email}>
                        <Td fontSize="sm">{r.email}</Td>
                        <Td><Badge colorScheme={tone.scheme} borderRadius="md">{tone.label}</Badge></Td>
                        <Td fontSize="xs" color={r.status === 'failed' ? 'red.600' : 'gray.500'}>
                          {r.status === 'failed' ? r.error : r.status === 'sent' ? r.sentAt && new Date(r.sentAt).toLocaleString('fr-FR') : '—'}
                        </Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </ModalBody>
        {c.failedCount > 0 && (c.status === 'completed' || c.status === 'failed') && (
          <ModalFooter borderTopWidth="1px" borderColor="gray.100">
            <Button leftIcon={<LuRefreshCw />} colorScheme="orange" size="sm" onClick={onRetryFailed}>
              Réessayer les {c.failedCount} échec(s)
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  )
}

function StatChip({ label, value, scheme }: { label: string; value: number; scheme: string }) {
  return (
    <Box borderWidth="1px" borderColor={`${scheme}.100`} bg={`${scheme}.50`} borderRadius="lg" p={2} textAlign="center">
      <Text fontSize="lg" fontWeight="800" color={`${scheme}.600`} lineHeight={1}>{value}</Text>
      <Text fontSize="2xs" color={`${scheme}.700`} textTransform="uppercase" letterSpacing="0.03em">{label}</Text>
    </Box>
  )
}
